import * as React from 'react';
import { useState } from 'react';

import { ThemeProvider } from '@mui/material';
import { Field, Formik } from 'formik';

import StyledFormWrapper from '../../../../shared/components/styled-form-wrapper/styled-form-wrapper';
import { useAppSelector } from '../../../../store/hooks';
import { FileMetadataTypesEnum } from '../../../shared/enums/file-metadata-types.enum';
import { useCheckPath } from '../../../shared/hooks/use-check-path/use-check-path.hook';
import { IdFile } from '../../../shared/models/id-file.model';
import { formTheme } from '../../../shared/themes/form-theme/form.theme';
import {
  resetGameThumbnail,
  resetGameZip,
  setGameThumbnail,
  setGameZip,
} from '../../store/slices/web-game.slice';
import { mapWebGameOrientationEnumToItemLabelModel } from '../../utils/mapping.util';
import { ActionButtons } from './components/action-buttons';
import { FormikDragAndDrop } from './components/formik-drag-and-drop/formik-drag-and-drop';
import { FormikInputField } from './components/formik-input-field';
import { FormikSelectField } from './components/formik-select-field';
import { SyncGameFilesHelper } from './components/sync-game-files-helper/sync-game-files-helper';
import { WebGameOrientationEnum } from './enums/web-game-orientation.enum';
import { useHandleGameSubmit } from './hooks/use-handle-game-submit.hook';
import { usePopulateGameDetails } from './hooks/use-populate-game-details.hook';
import { WebGameFormikModel } from './models/web-game-formik.model';
import { webGameFormValidationSchema } from './schema/web-game-form-validation.schema';

export const WebGameFormContainer = () => {
  const [isOnEdit, { id }] = useCheckPath('/game-center-management/edit/');

  const [thumbnailFile, setThumbnailFile] = useState<IdFile[] | undefined>();
  const [zipFile, setZipFile] = useState<IdFile[] | undefined>();

  const gameFields = useAppSelector(state => state.webGames.data);
  const thumbnailFileId = useAppSelector(
    state => state.webGames.thumbnailFileId
  );
  const zipFileId = useAppSelector(state => state.webGames.zipFileId);

  const handleSubmit = useHandleGameSubmit(
    isOnEdit,
    thumbnailFileId,
    zipFileId,
    id
  );

  const orientationOptions = [
    WebGameOrientationEnum.BOTH,
    WebGameOrientationEnum.LANDSCAPE,
    WebGameOrientationEnum.VERTICAL,
  ];

  usePopulateGameDetails(isOnEdit, setThumbnailFile, setZipFile, id);

  if (isOnEdit && !gameFields) {
    return null;
  }

  return (
    <ThemeProvider theme={formTheme}>
      <Formik<WebGameFormikModel>
        key={isOnEdit ? id : 'add-new-game'}
        initialValues={{
          title: gameFields?.title || '',
          genre: gameFields?.genre || '',
          orientation: gameFields?.orientation || '',
          size: gameFields?.size || '',
          thumbnail: thumbnailFileId ?? null,
          zip: zipFileId ?? null,
        }}
        validationSchema={webGameFormValidationSchema(
          thumbnailFileId,
          zipFileId
        )}
        onSubmit={handleSubmit}
        validateOnBlur={true}
        validateOnChange={true}
        validateOnMount={false}
      >
        {() => {
          return (
            <StyledFormWrapper>
              <SyncGameFilesHelper
                thumbnailFileId={thumbnailFileId}
                zipFileId={zipFileId}
              >
                <Field
                  name="title"
                  label="Title"
                  required
                  component={FormikInputField}
                />

                <Field
                  name="thumbnail"
                  label="Thumbnail"
                  fileType={FileMetadataTypesEnum.ICON}
                  component={FormikDragAndDrop}
                  fileTypeAccepted=".jpeg, .jpg, .png"
                  setFileHandler={setGameThumbnail}
                  clearFileHandler={resetGameThumbnail}
                  fieldDescription="Drop a PNG or JPG file here to upload"
                  {...(thumbnailFile && {
                    files: thumbnailFile,
                  })}
                />
                <Field
                  name="zip"
                  label="Zip"
                  fileType={FileMetadataTypesEnum.DOCUMENT}
                  component={FormikDragAndDrop}
                  fileTypeAccepted=".zip"
                  setFileHandler={setGameZip}
                  clearFileHandler={resetGameZip}
                  fieldDescription="Drop a ZIP file here to upload"
                  {...(zipFile && {
                    files: zipFile,
                  })}
                />

                <Field
                  name="genre"
                  label="Genre"
                  component={FormikInputField}
                />
                <Field name="size" label="Size" component={FormikInputField} />
                <Field
                  name="orientation"
                  label="Orientation"
                  component={FormikSelectField}
                  options={orientationOptions.map(
                    mapWebGameOrientationEnumToItemLabelModel
                  )}
                />

                <ActionButtons />
              </SyncGameFilesHelper>
            </StyledFormWrapper>
          );
        }}
      </Formik>
    </ThemeProvider>
  );
};
